// WbHauliersPage — /weighbridge/hauliers (admin only).
//
// Phase C3 Group 1. Lists the tenant's hauliers from
// /api/v1/wb/hauliers. Server-side paginated and searchable by name.
// Row click navigates to the detail/edit page; "+ Add Haulier"
// routes to /new.

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Truck } from 'lucide-react';
import { Card } from '../../../components/ui/Card/Card';
import { Button } from '../../../components/ui/Button/Button';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { Badge } from '../../../components/ui/Badge/Badge';
import { EmptyState } from '../../../components/ui/EmptyState/EmptyState';
import { Spinner } from '../../../components/ui/Spinner/Spinner';
import {
  useWbHauliers,
  type LicenceExpiryStatus,
} from '../hooks/useWbHauliers';
import { extractErrorMessage } from '../../../utils/errorMessage';
import styles from './WbHauliersPage.module.css';

const PER_PAGE = 50;

function LicenceBadge({ status }: { status: LicenceExpiryStatus }): JSX.Element {
  switch (status) {
    case 'valid':
      return <Badge variant="success">Valid</Badge>;
    case 'expiring_soon':
      return <Badge variant="warning">Expiring soon</Badge>;
    case 'expired':
      return <Badge variant="danger">Expired</Badge>;
    case 'not_set':
    default:
      return <Badge variant="muted">—</Badge>;
  }
}

export function WbHauliersPage(): JSX.Element {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);

  // Debounce the search input — 300ms feels responsive without spamming
  // the server. Reset to page 1 whenever the search term changes.
  useEffect(() => {
    const handle = window.setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 300);
    return () => window.clearTimeout(handle);
  }, [search]);

  const params = useMemo(
    () => ({
      page,
      per_page: PER_PAGE,
      ...(debouncedSearch ? { name: debouncedSearch } : {}),
    }),
    [page, debouncedSearch],
  );

  const hauliersQuery = useWbHauliers(params);
  const hauliers = hauliersQuery.data?.hauliers ?? [];
  const pagination = hauliersQuery.data?.pagination;
  const totalPages = pagination?.total_pages ?? 1;
  const hasNoSearch = debouncedSearch === '';

  return (
    <div className={styles.page}>
      <PageHeader
        title="Hauliers"
        subtitle="Transport carriers used for weighbridge loads"
        actions={
          <Button
            variant="primary"
            icon={Plus}
            onClick={() => navigate('/weighbridge/hauliers/new')}
          >
            Add Haulier
          </Button>
        }
      />

      <div className={styles.toolbar}>
        <input
          className={styles.search}
          type="search"
          placeholder="Search by name…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search hauliers by name"
        />
      </div>

      {hauliersQuery.isError ? (
        <p className={styles.error}>{extractErrorMessage(hauliersQuery.error)}</p>
      ) : null}

      <Card flush>
        {hauliersQuery.isLoading ? (
          <div className={styles.loadingRow}>
            <Spinner label="Loading hauliers" />
          </div>
        ) : hauliers.length === 0 ? (
          hasNoSearch ? (
            <EmptyState
              icon={Truck}
              title="No hauliers yet"
              description="Add a haulier to start recording carrier party references."
              action={
                <Button
                  variant="primary"
                  icon={Plus}
                  onClick={() => navigate('/weighbridge/hauliers/new')}
                >
                  Add Haulier
                </Button>
              }
            />
          ) : (
            <EmptyState
              icon={Truck}
              title="No matching hauliers"
              description={`No hauliers match "${debouncedSearch}".`}
            />
          )
        ) : (
          <>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Code</th>
                  <th>Contact</th>
                  <th>Phone</th>
                  <th>Email</th>
                  <th>Licence</th>
                  <th>Status</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {hauliers.map((h) => (
                  <tr
                    key={h.enc_id}
                    onClick={() => navigate(`/weighbridge/hauliers/${h.enc_id}`)}
                  >
                    <td>{h.name}</td>
                    <td className={styles.codeCell}>{h.code}</td>
                    <td>
                      {h.contact_name ?? (
                        <span className={styles.muted}>—</span>
                      )}
                    </td>
                    <td>
                      {h.phone ?? <span className={styles.muted}>—</span>}
                    </td>
                    <td>
                      {h.email ?? <span className={styles.muted}>—</span>}
                    </td>
                    <td>
                      <LicenceBadge status={h.licence_expiry_status} />
                    </td>
                    <td>
                      {h.is_active ? (
                        <Badge variant="success">Active</Badge>
                      ) : (
                        <Badge variant="muted">Inactive</Badge>
                      )}
                    </td>
                    <td className={styles.actionsCell}>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/weighbridge/hauliers/${h.enc_id}`);
                        }}
                      >
                        Edit
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {pagination && totalPages > 1 ? (
              <div className={styles.pagination}>
                <span>
                  Page {pagination.page} of {totalPages} ({pagination.total} total)
                </span>
                <div className={styles.pageControls}>
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  >
                    Next
                  </Button>
                </div>
              </div>
            ) : null}
          </>
        )}
      </Card>
    </div>
  );
}
